import React, { useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  useDroppable,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  horizontalListSortingStrategy,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Lock, Unlock } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { apiClient } from '../App';

// Sortable Item wrapper component
export function SortableItem({ id, children, disabled, showHandle = true }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 100 : 'auto',
  };

  return (
    <div ref={setNodeRef} style={style} className="relative group">
      {showHandle && !disabled && (
        <div
          {...attributes}
          {...listeners}
          className="absolute -left-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing z-10 bg-zinc-800 rounded p-1"
        >
          <GripVertical className="w-4 h-4 text-zinc-400" />
        </div>
      )}
      {children}
    </div>
  );
}

// Horizontal sortable item (for stat cards)
export function SortableCard({ id, children, disabled }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className={`relative ${!disabled ? 'cursor-grab active:cursor-grabbing' : ''}`}
      {...(disabled ? {} : { ...attributes, ...listeners })}
    >
      {children}
    </div>
  );
}

// Sidebar sortable item with dedicated drag handle
export function SortableSidebarItem({ id, children, disabled }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 100 : 'auto',
  };

  return (
    <div 
      ref={setNodeRef} 
      style={style} 
      className="relative flex items-center"
    >
      {/* Dedicated drag handle */}
      {!disabled && (
        <div
          {...attributes}
          {...listeners}
          className="absolute left-0 top-0 bottom-0 w-8 flex items-center justify-center cursor-grab active:cursor-grabbing z-20 hover:bg-zinc-700/50 rounded-l"
          style={{ touchAction: 'none' }}
        >
          <GripVertical className="w-4 h-4 text-zinc-500" />
        </div>
      )}
      <div className={!disabled ? "pl-8 w-full" : "w-full"}>
        {children}
      </div>
    </div>
  );
}

// Hook to manage layout state
export function useCompanyLayout() {
  const [layout, setLayout] = useState({
    sidebar_order: null,
    dashboard_cards_order: null,
    dashboard_sections_order: null,
  });
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLayout();
  }, []);

  const fetchLayout = async () => {
    try {
      const res = await apiClient.get('/company/layout');
      setLayout(res.data);
    } catch (err) {
      console.log('Failed to load layout');
    }
    setLoading(false);
  };

  const saveLayout = async (newLayout) => {
    try {
      await apiClient.put('/company/layout', newLayout);
      setLayout(prev => ({ ...prev, ...newLayout }));
      toast.success('Layout saved for all users!');
    } catch (err) {
      toast.error('Failed to save layout');
    }
  };

  const reorderItems = (items, activeId, overId) => {
    const oldIndex = items.findIndex(item => item.id || item.key || item === activeId);
    const newIndex = items.findIndex(item => item.id || item.key || item === overId);
    
    if (oldIndex !== -1 && newIndex !== -1) {
      return arrayMove(items, oldIndex, newIndex);
    }
    return items;
  };

  return {
    layout,
    setLayout,
    editMode,
    setEditMode,
    loading,
    saveLayout,
    reorderItems,
    fetchLayout,
  };
}

// Edit Mode Toggle Button
export function LayoutEditToggle({ editMode, setEditMode, onSave, isAdmin }) {
  if (!isAdmin) return null;
  
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() => {
        if (editMode) {
          onSave();
        }
        setEditMode(!editMode);
      }}
      className={editMode 
        ? "border-green-500 text-green-400 hover:bg-green-500/10" 
        : "border-zinc-600 text-zinc-400 hover:text-white"
      }
    >
      {editMode ? (
        <>
          <Lock className="w-4 h-4 mr-2" />
          Save Layout
        </>
      ) : (
        <>
          <Unlock className="w-4 h-4 mr-2" />
          Edit Layout
        </>
      )}
    </Button>
  );
}

// Draggable Context Provider
export function DraggableContainer({ children, onDragEnd, disabled }) {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  if (disabled) {
    return <>{children}</>;
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
    >
      {children}
    </DndContext>
  );
}

export { SortableContext, verticalListSortingStrategy, horizontalListSortingStrategy, rectSortingStrategy, arrayMove, useDroppable };
